/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useRef, forwardRef, useMemo, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import { ButtonIcon, Typography } from "../";
import Icon from "../../icons";
import { itemsCenter, border, boxBorder, flexRow, displayInlineFlex, justifyCenter, positionRelative, userSelectNone, cursorNotAllowed, positionAbsolute, overflowHidden } from "../../styles/general";
import { hexToRGBA } from "../../styles/color-helper";
import { useTheme, useColor as colors } from "../../theme";
const {
  colors: {
    system: {
      active,
      rest,
      white,
      disabled: disabledCl,
      dark
    },
    semantic: {
      success: semanticSuccess,
      warning: semanticWarning,
      danger: semanticDanger,
      info: semanticInfo
    }
  },
  spacing
} = useTheme();
const colorMap = new Map([['default', active], ['primary', active], ['success', semanticSuccess], ['warning', semanticWarning], ['danger', semanticDanger], ['info', semanticInfo]]);
const iconSizeMap = new Map([['small', `${spacing(4)}px`], ['medium', `${spacing(5)}px`], ['large', `${spacing(6)}px`]]);
const typographySizeMap = new Map([['small', 'p3'], ['medium', 'p2'], ['large', 'p1']]);
const paddingSizeMap = new Map([['small', `0px ${spacing(1.5)}px`], ['medium', `0px ${spacing(2)}px`], ['large', `0px ${spacing(2.5)}px`]]);
const minHeightSizeMap = new Map([['small', `${spacing(5)}px`], ['medium', `${spacing(6)}px`], ['large', `${spacing(7)}px`]]);
const Chip = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  viewType,
  color: colorProps,
  size,
  label,
  className,
  onRemove,
  onClick,
  onDbClick,
  onDoubleClick,
  startIcon,
  endIcon,
  clearAble,
  disabled,
  readOnly,
  children,
  style,
  labelProps
}, reference) => {
  const ref = useRef(null);
  const IconRef = useRef(null);
  if (!onDbClick && onDoubleClick) onDbClick = onDoubleClick;
  let color = colorMap.get(colorProps) || colorProps;
  if (colors[color]) color = colors[color];
  const infoChip = {
    color: color,
    contentColor: viewType !== 'filled' ? color : white,
    backgroundColor: viewType !== 'filled' ? white : color,
    iconSize: iconSizeMap.get(size),
    paddingSize: paddingSizeMap.get(size),
    typographySize: typographySizeMap.get(size),
    minHeightSize: minHeightSizeMap.get(size),
    viewType
  };
  infoChip.border = viewType === 'ghost' ? 'transparent !important' : colorProps === 'default' ? rest : infoChip === null || infoChip === void 0 ? void 0 : infoChip.color;
  infoChip.contentColor = colorProps === 'default' && viewType === 'outlined' ? rest : infoChip === null || infoChip === void 0 ? void 0 : infoChip.contentColor;
  infoChip.backgroundColor = colorProps === 'default' && viewType === 'filled' ? rest : infoChip === null || infoChip === void 0 ? void 0 : infoChip.backgroundColor;
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    const _instance = {}; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const onRemoveHandler = () => {
    if (onRemove || disabled) onRemove(ref.current);
  };
  const _onClick = event => {
    onClick && event.stopPropagation();
    if (disabled) return;
    onClick && onClick(event);
  };
  const _onDbClick = event => {
    onDbClick && event.stopPropagation();
    if (disabled) return;
    onDbClick && onDbClick(event);
  };

  // Start Icon
  const startIconView = useMemo(() => {
    if (typeof startIcon === 'string') {
      return jsx(Icon, {
        name: startIcon,
        width: infoChip.iconSize,
        height: infoChip.iconSize,
        color: 'currentColor'
      });
    } else return jsx("div", {
      css: ChipIconCSS(infoChip)
    }, startIcon);
  }, [startIcon, size]);

  // Label
  const labelView = useMemo(() => {
    return jsx(Typography, {
      css: ChipLabelCSS,
      type: infoChip.typographySize,
      color: 'inherit',
      lineClamp: 1,
      ...labelProps
    }, label);
  }, [label, size, startIcon, endIcon, clearAble]);

  // End Icon
  const endIconView = useMemo(() => {
    let node = endIcon;
    if (typeof node === 'string') {
      return jsx(Icon, {
        name: node,
        width: infoChip.iconSize,
        height: infoChip.iconSize,
        color: infoChip.contentColor,
        viewBox: true
      });
    }
    return clearAble ? jsx(ButtonIcon, {
      ref: IconRef,
      className: 'end-icon',
      viewType: 'ghost',
      name: 'Close',
      width: infoChip.iconSize,
      height: infoChip.iconSize,
      color: infoChip.contentColor,
      disabled: disabled,
      viewBox: true,
      onClick: onRemoveHandler
    }) : node && jsx("div", {
      css: ChipIconCSS(infoChip),
      className: 'end-icon ' + size,
      ref: IconRef
    }, node);
  }, [clearAble, viewType, color, endIcon, size, disabled, onRemove]);
  return jsx("div", {
    css: ChipRootCSS(onClick, onDbClick, colorProps, infoChip),
    ref: ref,
    className: ['DGN-UI-Chip', className, disabled && 'chip--disabled', readOnly && 'chip--readOnly', viewType === 'filled' && 'chip--filled'].join(' '),
    style: style,
    onClick: _onClick,
    onDoubleClick: _onDbClick
  }, !!startIcon && startIconView, !!label && labelView, (!!endIcon || !!clearAble) && endIconView, children);
}));
const ChipLabelCSS = css`
    ${flexRow};
    ${itemsCenter};
    ${positionRelative};
    ${userSelectNone};
`;
const ChipRootCSS = (onClick, onDbClick, colorProps, info) => css`
    ${displayInlineFlex};
    ${itemsCenter};
    ${positionRelative};
    ${boxBorder};
    ${border('1px', info.border)};
    width: max-content;
    padding: ${info === null || info === void 0 ? void 0 : info.paddingSize};
    color: ${info === null || info === void 0 ? void 0 : info.contentColor};
    background: ${info === null || info === void 0 ? void 0 : info.backgroundColor};
    border-radius: ${spacing(4)}px;
    cursor: ${onClick || onDbClick ? 'pointer' : 'unset'};
    height: ${info === null || info === void 0 ? void 0 : info.minHeightSize};
    min-height: ${info === null || info === void 0 ? void 0 : info.minHeightSize};
    gap: ${spacing()}px;
    &.chip--filled {
        &:not(.chip--disabled):not(.chip--readOnly) {
            &::after {
                border-radius: ${spacing(4)}px;
                ${positionAbsolute};
                top: 0;
                left: 0;
                content: '';
                width: 100%;
                height: 100%;
            }
            &:hover::after,
            &:focus::after {
                background: ${hexToRGBA(dark, 0.15)};
            }
            &:active::after {
                background: ${hexToRGBA(dark, 0.3)};
            }
        }
    }
    &:not(.chip--filled) {
        &:not(.chip--disabled):not(.chip--readOnly):hover {
            ${border('1px', info === null || info === void 0 ? void 0 : info.color)};
            background: ${hexToRGBA(info === null || info === void 0 ? void 0 : info.color, 0.1)};
            color: ${info === null || info === void 0 ? void 0 : info.color};
        }
        &:not(.chip--disabled):not(.chip--readOnly):active {
            ${border('1px', info === null || info === void 0 ? void 0 : info.color)};
            background: ${hexToRGBA(info === null || info === void 0 ? void 0 : info.color, 0.3)};
            color: ${info === null || info === void 0 ? void 0 : info.color};
        }
        &:not(.chip--disabled):not(.chip--readOnly):focus {
            ${border('1px', info === null || info === void 0 ? void 0 : info.color)};
            background: ${hexToRGBA(info === null || info === void 0 ? void 0 : info.color, 0.2)};
            color: ${info === null || info === void 0 ? void 0 : info.color};
        }
    }
    &.chip--disabled {
        color: ${disabledCl} !important;
        background-color: ${white} !important;
        ${border('1px', disabledCl)};
    }
    &.chip--readOnly,
    &.chip--disabled {
        ${cursorNotAllowed}
    }
`;
const ChipIconCSS = info => css`
    ${flexRow};
    ${justifyCenter};
    ${itemsCenter};
    ${positionRelative};
    ${boxBorder};
    ${overflowHidden};
    width: ${info === null || info === void 0 ? void 0 : info.iconSize};
    min-width: ${info === null || info === void 0 ? void 0 : info.iconSize};
    max-width: ${info === null || info === void 0 ? void 0 : info.iconSize};
    height: ${info === null || info === void 0 ? void 0 : info.iconSize};
    min-height: ${info === null || info === void 0 ? void 0 : info.iconSize};
    max-height: ${info === null || info === void 0 ? void 0 : info.iconSize};
`;
Chip.defaultProps = {
  viewType: 'outlined',
  size: 'medium',
  color: 'default'
};
Chip.propTypes = {
  /** content to display in Chip */
  label: PropTypes.string,
  /** class of Chip element */
  className: PropTypes.string,
  /** max length content to display was allowed */
  /** style inline of component */
  style: PropTypes.object,
  /** the icon display at head of Chip */
  startIcon: PropTypes.any,
  /** the icon display at last of Chip */
  endIcon: PropTypes.any,
  /** type of chip */
  viewType: PropTypes.oneOf(['ghost', 'outlined', 'filled']),
  /** color for text or background of Chip */
  color: PropTypes.oneOfType([PropTypes.oneOf(['default', 'primary', 'info', 'success', 'warning', 'danger']), PropTypes.string]),
  /** size of Chip */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** show clear icon if true */
  clearAble: PropTypes.bool,
  /** prevent all event and change color if true */
  disabled: PropTypes.bool,
  /** prevent all event if true */
  readOnly: PropTypes.bool,
  /** the function to get ref of Chip */
  refs: PropTypes.func,
  /** the function will run after remove chip */
  onRemove: PropTypes.func,
  /** the function will run when click on chip */
  onClick: PropTypes.func,
  /** the function will run when double click on chip */
  onDbClick: PropTypes.func,
  /** like onDbClick (priority onDbClick) */
  onDoubleClick: PropTypes.func,
  /** content to display on Chip (the same to label) */
  children: PropTypes.node,
  /** props for Typography of label */
  labelProps: PropTypes.object
};
export default Chip;